import { useEffect, useState } from "react";

import { type ShoppingItemView, api } from "../lib/api";
import { useLang } from "../lib/i18n";
import { type CategoryKey, type UnitKey, pantryCopy } from "../lib/i18n/pantry";
import { ScreenShell } from "./screen-shell";
import { IconCheck, IconPlus } from "./ui/icon";
import { Eyebrow, H1, MonoMeta, TopBar } from "./ui/primitives";

type Props = {
  householdId: string;
  onAdd: () => void;
  onBack: () => void;
};

// Localizes a stored unit/category to the active language; unknown values
// (free-form from AI) are shown verbatim.
function unitLabel(unit: string, t: (typeof pantryCopy)[keyof typeof pantryCopy]): string {
  return unit in t.unitLabel ? t.unitLabel[unit as UnitKey] : unit;
}

function categoryLabel(category: string, t: (typeof pantryCopy)[keyof typeof pantryCopy]): string {
  return category in t.categoryLabel ? t.categoryLabel[category as CategoryKey] : category;
}

function formatQty(item: ShoppingItemView, t: (typeof pantryCopy)[keyof typeof pantryCopy]): string {
  if (item.quantity === null && !item.unit) return "";
  if (item.quantity === null) return item.unit ? unitLabel(item.unit, t) : "";
  return `${item.quantity}${item.unit ? ` ${unitLabel(item.unit, t)}` : ""}`;
}

export function ShoppingListScreen({ householdId, onAdd, onBack }: Props) {
  const { lang } = useLang();
  const t = pantryCopy[lang];
  const SOURCE_LABEL: Record<ShoppingItemView["source"], string> = {
    manual: t.shoppingSourceManual,
    recipe: t.shoppingSourceRecipe,
    "pantry-empty": t.shoppingSourcePantryEmpty,
  };
  const [items, setItems] = useState<ShoppingItemView[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pendingId, setPendingId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api
      .listShopping(householdId)
      .then((res) => {
        if (!cancelled) setItems(res);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : t.errLoad);
      });
    return () => {
      cancelled = true;
    };
  }, [householdId]);

  async function toggle(item: ShoppingItemView) {
    setPendingId(item.id);
    setError(null);
    try {
      const updated = await api.updateShoppingItem(householdId, item.id, {
        completed: !item.completed,
      });
      setItems((prev) =>
        prev
          ? prev
              .map((i) => (i.id === item.id ? updated : i))
              .sort((a, b) =>
                a.completed === b.completed ? b.createdAt - a.createdAt : a.completed ? 1 : -1,
              )
          : prev,
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : t.errUpdate);
    } finally {
      setPendingId(null);
    }
  }

  async function remove(id: string) {
    setPendingId(id);
    setError(null);
    try {
      await api.deleteShoppingItem(householdId, id);
      setItems((prev) => (prev ? prev.filter((i) => i.id !== id) : prev));
    } catch (err) {
      setError(err instanceof Error ? err.message : t.errDelete);
    } finally {
      setPendingId(null);
    }
  }

  async function clearCompleted() {
    if (!items?.some((i) => i.completed)) return;
    setError(null);
    try {
      await api.clearCompletedShopping(householdId);
      setItems((prev) => prev?.filter((i) => !i.completed) ?? null);
    } catch (err) {
      setError(err instanceof Error ? err.message : t.shoppingErrClear);
    }
  }

  const pending = (items ?? []).filter((i) => !i.completed);
  const done = (items ?? []).filter((i) => i.completed);

  return (
    <ScreenShell hideHeader>
      <TopBar
        back={t.shoppingBack}
        title={t.shoppingTopTitle}
        onBack={onBack}
        right={
          done.length > 0 ? (
            <button
              type="button"
              onClick={clearCompleted}
              className="text-[13px] font-bold"
              style={{ color: "var(--accent)" }}
            >
              {t.shoppingClear}
            </button>
          ) : null
        }
      />

      <div style={{ paddingTop: 18, paddingBottom: 6 }}>
        <H1 accent=".">{t.shoppingTitle}</H1>
        <p style={{ marginTop: 12, fontSize: 14, color: "var(--ink-2)" }}>
          <span
            className="tabular"
            style={{
              fontFamily: "var(--font-display)",
              fontWeight: 800,
              color: "var(--ink)",
            }}
          >
            {t.shoppingCountToBuy(pending.length)}
          </span>
          {done.length > 0 ? t.shoppingDoneNote(done.length) : ""}
        </p>
      </div>

      {error ? (
        <p
          className="text-[12px] px-3 py-2 rounded-md"
          style={{ background: "var(--bad-bg)", color: "var(--bad-fg)", marginTop: 12 }}
        >
          {error}
        </p>
      ) : null}

      {items === null ? (
        <p style={{ marginTop: 24, fontSize: 13, color: "var(--muted)" }}>{t.loading}</p>
      ) : pending.length === 0 && done.length === 0 ? (
        <p style={{ marginTop: 24, fontSize: 13, color: "var(--muted)" }}>{t.shoppingEmpty}</p>
      ) : (
        <div style={{ paddingBottom: 120 }}>
          {pending.length > 0 ? (
            <section style={{ marginTop: 22 }}>
              <Eyebrow>{t.shoppingSectionToBuy}</Eyebrow>
              <div style={{ marginTop: 10 }}>
                {pending.map((item) => {
                  const meta = [
                    formatQty(item, t),
                    item.category ? categoryLabel(item.category, t) : "",
                    SOURCE_LABEL[item.source],
                  ]
                    .filter(Boolean)
                    .join(" · ");
                  return (
                    <div
                      key={item.id}
                      className="py-3.5 flex items-center gap-3"
                      style={{ borderTop: "1px solid var(--nav-border)" }}
                    >
                      <button
                        type="button"
                        onClick={() => toggle(item)}
                        disabled={pendingId === item.id}
                        className="shrink-0 transition-colors disabled:opacity-50"
                        style={{
                          width: 22,
                          height: 22,
                          borderRadius: 6,
                          boxShadow: "inset 0 0 0 1.5px var(--ink-2)",
                          background: "transparent",
                        }}
                        aria-label={t.shoppingCheckAria(item.name)}
                      />
                      <button
                        type="button"
                        onClick={() => toggle(item)}
                        disabled={pendingId === item.id}
                        className="flex-1 min-w-0 text-left disabled:opacity-50"
                      >
                        <div
                          className="text-[14px] font-semibold truncate"
                          style={{ color: "var(--ink)" }}
                        >
                          {item.name}
                        </div>
                        <div style={{ marginTop: 3 }}>
                          <MonoMeta>{meta.toUpperCase()}</MonoMeta>
                        </div>
                      </button>
                      <button
                        type="button"
                        onClick={() => remove(item.id)}
                        disabled={pendingId === item.id}
                        className="shrink-0 transition-colors disabled:opacity-50"
                        style={{
                          fontFamily: "var(--font-mono)",
                          fontSize: 11,
                          fontWeight: 600,
                          color: "var(--muted)",
                          padding: "4px 6px",
                        }}
                        aria-label={t.shoppingDeleteAria(item.name)}
                      >
                        {t.remove}
                      </button>
                    </div>
                  );
                })}
              </div>
            </section>
          ) : null}

          {done.length > 0 ? (
            <section style={{ marginTop: 26 }}>
              <Eyebrow>{t.shoppingSectionDone}</Eyebrow>
              <div style={{ marginTop: 10 }}>
                {done.map((item) => (
                  <div
                    key={item.id}
                    className="py-3.5 flex items-center gap-3"
                    style={{
                      borderTop: "1px solid var(--nav-border)",
                      opacity: 0.55,
                    }}
                  >
                    <button
                      type="button"
                      onClick={() => toggle(item)}
                      disabled={pendingId === item.id}
                      className="shrink-0 flex items-center justify-center transition-colors disabled:opacity-50"
                      style={{
                        width: 22,
                        height: 22,
                        borderRadius: 6,
                        background: "var(--ink)",
                        color: "var(--bg)",
                      }}
                      aria-label={t.shoppingUncheckAria(item.name)}
                    >
                      <IconCheck size={14} />
                    </button>
                    <button
                      type="button"
                      onClick={() => toggle(item)}
                      disabled={pendingId === item.id}
                      className="flex-1 min-w-0 text-left disabled:opacity-50"
                    >
                      <div
                        className="text-[14px] font-semibold truncate line-through"
                        style={{ color: "var(--ink)" }}
                      >
                        {item.name}
                      </div>
                      <div style={{ marginTop: 3 }}>
                        <MonoMeta>{formatQty(item, t).toUpperCase()}</MonoMeta>
                      </div>
                    </button>
                    <button
                      type="button"
                      onClick={() => remove(item.id)}
                      disabled={pendingId === item.id}
                      className="shrink-0 transition-colors disabled:opacity-50"
                      style={{
                        fontFamily: "var(--font-mono)",
                        fontSize: 11,
                        fontWeight: 600,
                        color: "var(--muted)",
                        padding: "4px 6px",
                      }}
                      aria-label={t.shoppingDeleteAria(item.name)}
                    >
                      {t.remove}
                    </button>
                  </div>
                ))}
              </div>
            </section>
          ) : null}
        </div>
      )}

      {/* sticky CTA — Dodaj rzecz */}
      <div
        className="safe-bottom"
        style={{
          position: "sticky",
          bottom: 0,
          left: 0,
          right: 0,
          marginTop: "auto",
          paddingTop: 12,
          paddingBottom: 12,
          background:
            "linear-gradient(to top, var(--bg) 60%, color-mix(in srgb, var(--bg) 0%, transparent))",
        }}
      >
        <button
          type="button"
          onClick={onAdd}
          className="w-full inline-flex items-center justify-center gap-2 uppercase transition-all"
          style={{
            padding: "17px 20px",
            borderRadius: 12,
            background: "var(--accent)",
            color: "var(--accent-ink)",
            fontSize: 14,
            fontWeight: 700,
            letterSpacing: "0.02em",
            boxShadow: "var(--shadow-cta)",
          }}
        >
          <IconPlus size={16} />
          {t.shoppingCta}
        </button>
      </div>
    </ScreenShell>
  );
}
